import { Command } from 'cmdk';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  EmptyStateGraphic,
  Spinner,
  KeyboardShortcut,
  InlineStack,
  type ModalPrimitiveProps,
  type PlumaManagedModalProps,
  Filters,
  FiltersAddButton,
  FiltersActiveList,
  Button,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useIsAgentFullscreen } from '@/features/copilot';
import type { useCopilotSessionContext, useCopilotSidebar } from '@/features/copilot';
import { useListSourceOptions } from '@/features/pipelines/sources/hooks';
import { useListDestinationOptions } from '@/features/pipelines/destinations/hooks';
import { getServicesQueryOptions, encodeSearchFilters } from '@/services/api';
import { useQueries } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import type { useCurrentWorkspace } from '@/context/current-workspace';
import type { useThemeContext } from '@/context/theme';
import type { useSideNavState, useToggleSideNav } from '@customerio/pluma-components/react';
import * as styles from './index.css';
import type { SearchResult, TemplateType } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  LABEL,
  SEARCH_RESULT_HEADINGS,
  SEARCH_KIND_FILTERS,
  COMMAND_DIALOG_QUERY_DEFAULTS,
  TEMPLATE_TYPE_FILTER_OPTIONS,
} from './constants';
import type { SearchKindFilter } from './constants';
import {
  computeItemPosition,
  buildPeopleSearchQuery,
  buildIntegrationCatalog,
  filterAndCapIntegrations,
  groupSearchResults,
  mapCustomerToSearchResult,
  mapPipelinesConnectionToSearchResult,
  remapUnsetTemplates,
  isEmailQuery,
  type IntegrationCatalog,
} from './utils';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { useSettingsCommands } from './use-settings-commands';
import { useLocalSearchResults } from './use-local-search-results';
import { navigationSearchOptions, type PermissionGuard } from './use-navigation-search';
import { docsSearchOptions } from './use-docs-search';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { PlumaModal as PlumaModalCss } from '@customerio/pluma-components/css/components';
import type { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useKeyHold } from '@tanstack/react-hotkeys';
import { QuickLookPanel } from './quick-look';
import { SearchResultItem } from './search-result-item';
import { LocalCommandsSection } from './local-commands-section';
import clsx from 'clsx';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

type ActiveCategory = 'all' | 'people' | 'docs' | 'integrations' | SearchKindFilter;

const NO_SOURCES = { search: false, people: false, docs: false, navigation: false, integrations: false };

const sharedQueryOptions = {
  ...COMMAND_DIALOG_QUERY_DEFAULTS,
  forwardAbortSignal: true,
} as const;

type CommandDialogData = {
  session: ReturnType<typeof useCopilotSessionContext>;
  workspace: ReturnType<typeof useCurrentWorkspace>;
  initialInputValue?: string;
  initialActiveFilter?: string;
  initialActiveSubFilter?: string | null;
  initialIsFiltersVisible?: boolean;
  initialIsQuickLookOpen?: boolean;
  persistInputValue: (value: string) => void;
  persistActiveFilter: (value: string) => void;
  persistActiveSubFilter: (value: string | null) => void;
  persistFiltersVisibility: (value: boolean) => void;
  persistQuickLookOpen: (value: boolean) => void;
  track: ReturnType<typeof useAnalytics>['track'];
  openCopilot: ReturnType<typeof useCopilotSidebar>['openCopilot'];
  colorScheme: ReturnType<typeof useThemeContext>['colorScheme'];
  setColorScheme: ReturnType<typeof useThemeContext>['setColorScheme'];
  canToggleTheme: ReturnType<typeof useThemeContext>['canToggleTheme'];
  sideNavState: ReturnType<typeof useSideNavState>;
  toggleSideNav: ReturnType<typeof useToggleSideNav>;
  isMasqWithoutAccess: boolean;
  isBuilder: boolean;
  guard: PermissionGuard | null;
};

export function CommandDialog({ data, onClose, modalState }: PlumaManagedModalProps<CommandDialogData>) {
  const { isOpen: isDialogOpen, onClose: onModalManagerClose, onCloseComplete, modalContext } = modalState;
  const isUniversalSearchMultiEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchMulti);
  const isAiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isAgentFullscreen = useIsAgentFullscreen();
  const showAskAssistant = isAiEnabled && !isAgentFullscreen;
  const safeNavigate = useSafeNavigate();
  const [inputValue, setInputValue] = useState(data.initialInputValue ?? '');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 250);

  const isQuickLookEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchQuickLook);
  const [selectedValue, setSelectedValue] = useState('');

  const isMetaHeld = useKeyHold('Meta');
  const isCtrlHeld = useKeyHold('Control');

  const {
    session,
    workspace,
    persistInputValue,
    persistActiveFilter,
    persistActiveSubFilter,
    persistFiltersVisibility,
    persistQuickLookOpen,
    track,
    openCopilot,
    isMasqWithoutAccess,
    isBuilder,
    guard,
  } = data;
  const workspaceId = workspace?.id ?? 0;
  const catalogWorkspaceId = workspaceId > 0 ? workspaceId : null;
  const sourceOptionsQuery = useListSourceOptions(catalogWorkspaceId);
  const destinationOptionsQuery = useListDestinationOptions(catalogWorkspaceId);

  // Each query resolves independently; the map is partial until both settle.
  // During that window, types from the slower catalog show fallback icons/names briefly.
  const catalogMap = useMemo<IntegrationCatalog>(
    () => buildIntegrationCatalog(sourceOptionsQuery.data ?? [], destinationOptionsQuery.data ?? []),
    [sourceOptionsQuery.data, destinationOptionsQuery.data],
  );

  const handleInputFocus = useCallback((event: Event & { target: HTMLInputElement }) => {
    event.target.select();
  }, []);

  const queryStartTimeRef = useRef<number | null>(null);
  const previousDebouncedInputRef = useRef(debouncedInputValue);

  const openTimeRef = useRef(Date.now());
  const previousIsDialogOpenRef = useRef(isDialogOpen);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(data.initialIsFiltersVisible ?? true);

  // used for tracking the time the user started typing to get the time to first result
  if (debouncedInputValue !== previousDebouncedInputRef.current) {
    previousDebouncedInputRef.current = debouncedInputValue;
    if (debouncedInputValue.length > 0) {
      queryStartTimeRef.current = Date.now();
    }
  }
  // used for tracking the time the dialog was opened to get the time open
  if (isDialogOpen !== previousIsDialogOpenRef.current) {
    previousIsDialogOpenRef.current = isDialogOpen;
    if (isDialogOpen === true) {
      openTimeRef.current = Date.now();
    }
  }

  const isSearchingEnabled = isDialogOpen === true && debouncedInputValue.length > 0 && workspaceId > 0;

  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(() => {
    if (!isUniversalSearchMultiEnabled) return 'all';
    const stored = data.initialActiveFilter;
    if (isMasqWithoutAccess && stored === 'people') return 'all';
    if (isBuilder && (stored === SEARCH_KIND_FILTERS.campaigns || stored === SEARCH_KIND_FILTERS.actions)) return 'all';
    const validCategories: string[] = ['all', 'people', 'docs', 'integrations', ...Object.values(SEARCH_KIND_FILTERS)];
    return validCategories.includes(stored ?? '') ? (stored as ActiveCategory) : 'all';
  });

  const [activeTemplateType, setActiveTemplateType] = useState<TemplateType | null>(() => {
    if (data.initialActiveFilter !== SEARCH_KIND_FILTERS.templates) return null;
    const stored = data.initialActiveSubFilter;
    const validTemplateTypes = TEMPLATE_TYPE_FILTER_OPTIONS.map((o) => o.value as string);
    return stored != null && validTemplateTypes.includes(stored) ? (stored as TemplateType) : null;
  });

  const isPeopleFilterActive = activeCategory === 'people';
  const isDocsFilterActive = activeCategory === 'docs';
  const activeKindFilter = (Object.values(SEARCH_KIND_FILTERS) as string[]).includes(activeCategory)
    ? activeCategory
    : null;

  const activeSources = useMemo<typeof NO_SOURCES>(() => {
    const hasNavigation = activeCategory === 'all' || activeCategory === 'navigation';
    // Navigation is handled locally — skip the API entirely to avoid stale Vertex results
    if (activeCategory === 'navigation') return { ...NO_SOURCES, navigation: true };
    if (isPeopleFilterActive) return { ...NO_SOURCES, people: true };
    if (isDocsFilterActive) return { ...NO_SOURCES, docs: true };
    if (activeCategory === 'integrations') return { ...NO_SOURCES, integrations: true };
    if (activeKindFilter != null) return { ...NO_SOURCES, search: true };
    return {
      search: true,
      people: isUniversalSearchMultiEnabled,
      docs: isUniversalSearchMultiEnabled,
      navigation: hasNavigation,
      integrations: isUniversalSearchMultiEnabled,
    };
  }, [activeCategory, isPeopleFilterActive, isDocsFilterActive, activeKindFilter, isUniversalSearchMultiEnabled]);

  const encodedFilters = useMemo(() => {
    if (activeKindFilter != null) {
      if (activeKindFilter === SEARCH_KIND_FILTERS.templates && activeTemplateType != null) {
        return encodeSearchFilters({ kind: [activeKindFilter], template_type: [activeTemplateType] });
      }
      return encodeSearchFilters({ kind: [activeKindFilter] });
    }
    return undefined;
  }, [activeKindFilter, activeTemplateType]);

  const peopleSearchTerm = useMemo(
    () => (activeSources.people ? buildPeopleSearchQuery(debouncedInputValue) : null),
    [activeSources.people, debouncedInputValue],
  );

  const [searchQuery, docsQuery, peopleQuery, navigationQuery, integrationsQuery] = useQueries({
    queries: [
      getServicesQueryOptions(
        'search',
        { params: { workspaceId }, query: { q: debouncedInputValue, filters: encodedFilters } },
        {
          enabled: isSearchingEnabled && activeSources.search,
          ...sharedQueryOptions,
          select: (data) => ({
            ...data,
            results: remapUnsetTemplates((data.results ?? []).filter((r) => r.kind !== 'navigation')),
          }),
        },
      ),
      {
        ...docsSearchOptions(debouncedInputValue),
        enabled: activeSources.docs && isDialogOpen === true && debouncedInputValue.length > 0,
      },
      getServicesQueryOptions(
        'getPeople',
        { params: { workspaceId }, query: { email: peopleSearchTerm ?? undefined, asc: false, page: 1 } },
        {
          enabled: isSearchingEnabled && activeSources.people && peopleSearchTerm != null && !isMasqWithoutAccess,
          ...sharedQueryOptions,
          // Customers endpoint has no page size param (hardcoded 50 server-side); slice to match other sources.
          select: (data) =>
            data.customers.slice(0, 10).map((customer) => mapCustomerToSearchResult(customer, workspaceId)),
        },
      ),
      {
        ...navigationSearchOptions(debouncedInputValue, workspaceId, guard),
        enabled: isSearchingEnabled && activeSources.navigation,
      },
      getServicesQueryOptions(
        'listIntegrationsPipelines',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.integrations,
          ...sharedQueryOptions,
          // The endpoint returns all connections with no server-side filter or pagination.
          // Mapping is cheap; the render-side cap is applied after fuzzy-filtering in
          // `filteredIntegrations` so destinations aren't dropped on workspaces with many sources.
          select: (data) => data.connections.map(mapPipelinesConnectionToSearchResult),
        },
      ),
    ],
  });

  const sources = {
    search: searchQuery,
    people: peopleQuery,
    docs: docsQuery,
    navigation: navigationQuery,
    integrations: integrationsQuery,
  } satisfies Record<
    keyof typeof activeSources,
    { isError: boolean; isLoading: boolean; isFetching: boolean; dataUpdatedAt: number }
  >;

  const activeSourceStates = (Object.keys(activeSources) as (keyof typeof activeSources)[])
    .filter((key) => activeSources[key])
    .map((key) => sources[key]);

  const isError = activeSourceStates.some((s) => s.isError);
  const isLoading = activeSourceStates.some((s) => s.isLoading);
  const isQuickLookSourcesFetching = isSearchingEnabled && activeSourceStates.some((s) => s.isFetching);
  const dataUpdatedAt = Math.max(0, ...activeSourceStates.map((s) => s.dataUpdatedAt));

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [activeCategory, activeTemplateType]);

  const closeDialog = useCallback(() => {
    onClose();
  }, [onClose]);

  const filteredIntegrations = useMemo(
    () => filterAndCapIntegrations(integrationsQuery.data ?? [], debouncedInputValue, catalogMap, 50),
    [integrationsQuery.data, debouncedInputValue, catalogMap],
  );

  const allApiResults = useMemo(() => {
    const results: SearchResult[] = [];
    // When the query looks like an email, surface people first — an exact profile match from the
    // customers endpoint would otherwise be buried under partial Vertex AI matches (e.g. campaigns
    // or templates whose content happens to contain the same address).
    const looksLikeEmail = isEmailQuery(debouncedInputValue);
    if (looksLikeEmail && activeSources.people) results.push(...(peopleQuery.data ?? []));
    if (activeSources.search) results.push(...(searchQuery.data?.results ?? []));
    if (!looksLikeEmail && activeSources.people) results.push(...(peopleQuery.data ?? []));
    if (activeSources.docs) results.push(...(docsQuery.data ?? []));
    if (activeSources.navigation) results.push(...(navigationQuery.data ?? []));
    if (activeSources.integrations) results.push(...filteredIntegrations);
    return results;
  }, [
    debouncedInputValue,
    activeSources,
    searchQuery.data?.results,
    peopleQuery.data,
    docsQuery.data,
    navigationQuery.data,
    filteredIntegrations,
  ]);

  const filteredApiResults = useMemo(
    () => (isBuilder ? allApiResults.filter((r) => r.kind !== 'campaign' && r.kind !== 'action') : allApiResults),
    [allApiResults, isBuilder],
  );

  const groupedResults = useMemo(() => groupSearchResults(filteredApiResults), [filteredApiResults]);

  const resultsByKey = useMemo(() => {
    const map = new Map<string, SearchResult>();
    for (const [, items] of groupedResults) {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;
        map.set(key, item);
      }
    }
    return map;
  }, [groupedResults]);

  const selectedResult = resultsByKey.get(selectedValue) ?? null;

  const handleValueChange = useCallback((value: string) => {
    setSelectedValue(value);
  }, []);

  // Ref, not state: changes to the preference shouldn't trigger re-renders, and reading it
  // inside the effect below doesn't need to be a dependency (we only care about its value
  // when results arrive, not on every preference change).
  const preferredQuickLookOpenRef = useRef(data.initialIsQuickLookOpen ?? true);
  const [isQuickLookOpen, setIsQuickLookOpen] = useState(false);
  const isQuickLookPeekable = isQuickLookEnabled && selectedResult != null && selectedResult.kind !== 'navigation';

  // Visibility follows persisted preference whenever a workspace search is active; syncing
  // with results also persists preference. Clearing the query or disabling search closes panel.
  // Preferred is read from preferredQuickLookOpenRef inside the effect (not a dependency). Re-sync when
  // workspace search gates, Quick Look flag, API result cardinality, or persister identity change — not fetch flags.
  useEffect(() => {
    if (!isQuickLookEnabled) return;

    if (!isSearchingEnabled) {
      // Skip closing quick look when the dialog itself is closing — avoids animating
      // the dialog width back to its default while the close animation is in progress.
      if (isDialogOpen === true) {
        setIsQuickLookOpen(false);
      }
      return;
    }

    const preferred = preferredQuickLookOpenRef.current;
    setIsQuickLookOpen(preferred);
    if (filteredApiResults.length > 0) {
      persistQuickLookOpen(preferred);
    }
  }, [filteredApiResults.length, isDialogOpen, isQuickLookEnabled, isSearchingEnabled, persistQuickLookOpen]);

  const setFiltersVisible = useCallback(
    (value: boolean) => {
      setIsFiltersVisible(value);
      persistFiltersVisibility(value);
      void track('universalSearch', 'filterVisibilityToggled', { Visible: value });
    },
    [persistFiltersVisibility, track],
  );

  const openQuickLook = useCallback(() => {
    if (selectedResult == null) return;
    preferredQuickLookOpenRef.current = true;
    persistQuickLookOpen(true);
    setIsQuickLookOpen(true);
    void track('universalSearch', 'previewOpened', {
      'Result Kind': selectedResult.kind,
      Query: debouncedInputValue,
    });
  }, [selectedResult, track, debouncedInputValue, persistQuickLookOpen]);

  const closeQuickLook = useCallback(() => {
    preferredQuickLookOpenRef.current = false;
    persistQuickLookOpen(false);
    setIsQuickLookOpen(false);
    void track('universalSearch', 'previewClosed', {
      'Result Kind': selectedResult?.kind ?? 'none',
      Query: debouncedInputValue,
    });
  }, [persistQuickLookOpen, track, selectedResult, debouncedInputValue]);

  const commandDialogFilters = useMemo(
    () => [
      {
        key: 'category',
        label: 'Category',
        type: 'radio' as const,
        isAlwaysShown: true,
        options: [
          { label: SEARCH_RESULT_HEADINGS.segment, value: SEARCH_KIND_FILTERS.segments },
          ...(!isBuilder
            ? [
                { label: SEARCH_RESULT_HEADINGS.campaign, value: SEARCH_KIND_FILTERS.campaigns },
                { label: SEARCH_RESULT_HEADINGS.action, value: SEARCH_KIND_FILTERS.actions },
              ]
            : []),
          { label: 'Templates', value: SEARCH_KIND_FILTERS.templates },
          { label: SEARCH_RESULT_HEADINGS.newsletter, value: SEARCH_KIND_FILTERS.newsletters },
          { label: SEARCH_RESULT_HEADINGS.goal, value: SEARCH_KIND_FILTERS.goals },
          { label: SEARCH_RESULT_HEADINGS.navigation, value: SEARCH_KIND_FILTERS.navigation },
          { label: 'Integrations', value: 'integrations' as const },
          ...(!isMasqWithoutAccess ? [{ label: SEARCH_RESULT_HEADINGS.customer, value: 'people' as const }] : []),
          { label: SEARCH_RESULT_HEADINGS.docs, value: 'docs' as const },
        ],
      },
      ...(activeCategory === SEARCH_KIND_FILTERS.templates
        ? [
            {
              key: 'type',
              label: 'Type',
              type: 'radio' as const,
              options: [...TEMPLATE_TYPE_FILTER_OPTIONS],
            },
          ]
        : []),
    ],
    [activeCategory, isMasqWithoutAccess, isBuilder],
  );

  const filterValues = useMemo(() => {
    const values: { key: string; value: string }[] = [];
    if (activeCategory !== 'all') values.push({ key: 'category', value: activeCategory });
    if (activeTemplateType != null) values.push({ key: 'type', value: activeTemplateType });
    return values;
  }, [activeCategory, activeTemplateType]);

  const handleFiltersChange = useCallback(
    (key: string, value: string) => {
      void track('universalSearch', 'filterChanged', {
        'Filter Key': key,
        'Filter Value': value,
        'Previous Filter': activeCategory,
        'Previous Sub Filter': activeTemplateType ?? 'none',
        Query: debouncedInputValue,
      });
      if (key === 'category') {
        setActiveCategory(value as ActiveCategory);
        persistActiveFilter(value);
        if (value !== SEARCH_KIND_FILTERS.templates) {
          setActiveTemplateType(null);
          persistActiveSubFilter(null);
        }
      } else if (key === 'type') {
        setActiveTemplateType(value as TemplateType);
        persistActiveSubFilter(value);
      }
    },
    [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue],
  );

  const handleFiltersClear = useCallback(
    (key: string) => {
      void track('universalSearch', 'filterChanged', {
        'Filter Key': key,
        'Filter Value': null,
        'Previous Filter': activeCategory,
        'Previous Sub Filter': activeTemplateType ?? 'none',
        Query: debouncedInputValue,
      });
      if (key === 'type') {
        setActiveTemplateType(null);
        persistActiveSubFilter(null);
      } else {
        setActiveCategory('all');
        persistActiveFilter('all');
        setActiveTemplateType(null);
        persistActiveSubFilter(null);
      }
    },
    [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue],
  );

  const handleFiltersClearAll = useCallback(() => {
    void track('universalSearch', 'filterChanged', {
      'Filter Key': 'category',
      'Filter Value': 'all',
      'Previous Filter': activeCategory,
      'Previous Sub Filter': activeTemplateType ?? 'none',
      Query: debouncedInputValue,
    });
    setActiveCategory('all');
    persistActiveFilter('all');
    setActiveTemplateType(null);
    persistActiveSubFilter(null);
  }, [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      persistInputValue(value);
    },
    [persistInputValue],
  );

  // Using inputValue instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(inputValue);
  const settingsCommands = useSettingsCommands(inputValue, {
    colorScheme: data.colorScheme,
    setColorScheme: data.setColorScheme,
    canToggleTheme: data.canToggleTheme,
    sideNavState: data.sideNavState,
    toggleSideNav: data.toggleSideNav,
    openCopilot: data.openCopilot,
  });

  // Local search results — handlers are called on-demand with the debounced query
  const localSearchGroups = useLocalSearchResults(debouncedInputValue);

  const apiResultsCount = filteredApiResults.length;
  const localSearchCount = localSearchGroups.reduce((sum, g) => sum + g.results.length, 0);
  const quickActionsCount = hydraCommands.length + settingsCommands.length;
  const hasSettingsCommands = activeCategory === 'all' && settingsCommands.length > 0;

  const hasNoApiResults = filteredApiResults.length === 0;
  const hasApiResults = filteredApiResults.length > 0 && !isLoading;
  const hasLocalCommands = activeCategory === 'all' && hydraCommands.length > 0;
  const hasLocalSearchResults = activeCategory === 'all' && localSearchGroups.length > 0;
  const hasNoResults = hasNoApiResults && !hasSettingsCommands && !hasLocalCommands && !hasLocalSearchResults;
  /** Mirrors Command.Empty rendering "No results found for …" — quick look should show No preview, not a stale selection */
  const universalSearchShowsNoResultsEmptyState =
    debouncedInputValue.length > 0 && !isLoading && (hasNoResults || (isError && filteredApiResults.length === 0));
  const askAssistantVisible =
    showAskAssistant && debouncedInputValue.length > 0 && !isLoading && !hasNoResults && !searchQuery.isError;

  const totalVisibleCount = useMemo(() => {
    const localCount = activeCategory === 'all' ? quickActionsCount + localSearchCount : 0;
    return (askAssistantVisible ? 1 : 0) + localCount + apiResultsCount;
  }, [askAssistantVisible, activeCategory, quickActionsCount, localSearchCount, apiResultsCount]);

  const timeToFirstResultMs =
    queryStartTimeRef.current != null ? Math.max(0, dataUpdatedAt - queryStartTimeRef.current) : 0;

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind'], position: number) => {
      const isExternal = kind === 'docs';
      const openInNewTab = isExternal || isMetaHeld || isCtrlHeld;

      void track('universalSearch', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
        'Open in New Tab': openInNewTab,
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
        if (!isMetaHeld && !isCtrlHeld) {
          closeDialog();
        }
      } else {
        safeNavigate(url);
        closeDialog();
      }
    },
    [
      safeNavigate,
      closeDialog,
      track,
      isMetaHeld,
      isCtrlHeld,
      debouncedInputValue,
      dataUpdatedAt,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleLocalSearchSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_search',
        'Result Kind': 'search',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleAskAssistant = useCallback(
    (resultPosition: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'ask_assistant',
        'Result Kind': 'assistant',
        Query: debouncedInputValue,
        'Result Position': resultPosition,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      openCopilot({ triggerSource: 'command_dialog' });
      if (debouncedInputValue.length > 0) {
        void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
      }
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      session,
      openCopilot,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('universalSearch', 'closed', {
        Reason: reason ?? 'dismiss',
        'Had Query': !isEmptyString(inputValue.trim()),
        Query: inputValue.trim(),
        'Time Open Ms': Date.now() - openTimeRef.current,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
      });
      onModalManagerClose?.(_event, reason);
    },
    [track, onModalManagerClose, inputValue, activeCategory, activeTemplateType],
  );

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={isDialogOpen ?? false}
      onClose={handleDialogClose}
      onCloseComplete={onCloseComplete}
      modalContext={modalContext}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog
          ref={dialogRef}
          className={clsx(styles.dialog, isQuickLookOpen && isQuickLookEnabled && styles.dialogQuickLookOpen)}
        >
          <Command loop label={LABEL} shouldFilter={false} value={selectedValue} onValueChange={handleValueChange}>
            <Box mx="200" mt="100" style={{ borderBottom: `1px solid ${themeVars.color['border-minimal']}` }}>
              <InlineStack
                alignBlock="center"
                alignInline="space-between"
                gap={isUniversalSearchMultiEnabled ? '050' : undefined}
              >
                <Command.Input asChild value={inputValue} onValueChange={handleInputChange}>
                  <TextField
                    ref={inputRef}
                    autoFocus={true}
                    placeholder={LABEL}
                    onInput={(event) => handleInputChange(event.target.value)}
                    onFocus={handleInputFocus}
                    ariaLabel={
                      isBuilder
                        ? 'Use the command palette to search for templates, segments, or anything else in this workspace.'
                        : 'Use the command palette to search for campaigns, templates, segments, or anything else in this workspace.'
                    }
                    className={`${styles.largeTextField} fs-mask`}
                    icon="search"
                    iconColor="accent"
                    size="sm"
                    w="full"
                  />
                </Command.Input>
                {isQuickLookEnabled ? (
                  <Tooltip
                    content={
                      isQuickLookOpen
                        ? 'Close quick look'
                        : hasNoResults
                          ? undefined
                          : isQuickLookPeekable === false
                            ? 'Cannot preview this result'
                            : 'Open quick look'
                    }
                    placement="bottom"
                    isCustom={true}
                  >
                    {(context) => (
                      <Button
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                        variant="tertiary"
                        size="sm"
                        icon={isQuickLookOpen ? 'view-off' : 'view-on'}
                        iconPosition="trailing"
                        onClick={isQuickLookOpen ? closeQuickLook : openQuickLook}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                            event.preventDefault();
                            if (isQuickLookOpen) {
                              closeQuickLook();
                            } else {
                              openQuickLook();
                            }
                            inputRef.current?.focus();
                          }
                        }}
                        isIconOnly={true}
                        isActive={isQuickLookOpen}
                        isDisabled={hasNoResults || (isQuickLookOpen === false && isQuickLookPeekable === false)}
                        mb="050"
                        aria-label={isQuickLookOpen ? 'Close quick look' : 'Open quick look'}
                      />
                    )}
                  </Tooltip>
                ) : null}
                {isUniversalSearchMultiEnabled ? (
                  <Tooltip
                    content={isFiltersVisible ? 'Hide filters' : 'Show filters'}
                    placement="right"
                    isCustom={true}
                  >
                    {(context) => (
                      <Button
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                        variant="tertiary"
                        size="sm"
                        icon="filter"
                        iconPosition="trailing"
                        onClick={() => setFiltersVisible(!isFiltersVisible)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                            event.preventDefault();
                            if (isFiltersVisible) {
                              setFiltersVisible(false);
                              inputRef.current?.focus();
                            } else {
                              setFiltersVisible(true);
                            }
                          }
                        }}
                        isIconOnly={true}
                        isActive={isFiltersVisible}
                        mb="050"
                        aria-label={isFiltersVisible ? 'Hide filters' : 'Show filters'}
                      />
                    )}
                  </Tooltip>
                ) : null}
              </InlineStack>
              {isUniversalSearchMultiEnabled ? (
                <div
                  className={clsx(styles.filtersWrapper, isFiltersVisible && styles.filtersWrapperOpen)}
                  inert={!isFiltersVisible ? true : undefined}
                >
                  {/* Stop non-Escape keys (Enter, arrows) from bubbling into Command.List, which would
                      select items or move focus out of the filters area. For Escape, we only block propagation
                      while a filter dropdown is open so Pluma can close it without also closing the modal.
                      Once the dropdown is closed, the next Escape bubbles up and closes the modal normally. */}
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keydown interception explained above */}
                  <div
                    className={styles.filtersInner}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        const hasOpenDropdown = event.currentTarget.querySelector('[aria-expanded="true"]') != null;
                        if (hasOpenDropdown) {
                          event.stopPropagation();
                        }
                        return;
                      }
                      event.stopPropagation();
                    }}
                  >
                    <InlineStack alignBlock="center" pb="100">
                      <Filters
                        filters={commandDialogFilters}
                        values={filterValues}
                        onChange={handleFiltersChange}
                        onClear={handleFiltersClear}
                        onClearAll={handleFiltersClearAll}
                        addButtonVariant="subtle"
                      >
                        <InlineStack alignBlock="center" gap="100">
                          <FiltersActiveList />
                          <FiltersAddButton />
                        </InlineStack>
                      </Filters>
                    </InlineStack>
                  </div>
                </div>
              ) : null}
            </Box>
            <Flex direction="row" overflow="hidden">
              <Box flex={1} minW="0" overflow="hidden">
                <Command.List ref={listRef}>
                  {(hasSettingsCommands || hasLocalCommands) && !hasApiResults ? (
                    <LocalCommandsSection
                      settingsCommands={settingsCommands}
                      hydraCommands={hydraCommands}
                      baseOffset={0}
                      onSelect={handleLocalCommandSelect}
                    />
                  ) : null}
                  {hasLocalSearchResults ? (
                    <>
                      {localSearchGroups.map((group, groupIndex) => {
                        const groupOffset = localSearchGroups
                          .slice(0, groupIndex)
                          .reduce((sum, g) => sum + g.results.length, 0);
                        return (
                          <Command.Group key={`search-${group.heading}`} heading={group.heading}>
                            {group.results.map((result, resultIndex) => (
                              <Command.Item
                                key={`search-${result.id}`}
                                value={`search-${result.id}`}
                                onSelect={() =>
                                  handleLocalSearchSelect(
                                    result.onSelect,
                                    computeItemPosition({
                                      hydraCommandsCount: hasApiResults ? 0 : quickActionsCount,
                                      localSearchCount,
                                      section: 'localSearch',
                                      groupOffset,
                                      itemIndex: resultIndex,
                                    }),
                                  )
                                }
                                asChild
                              >
                                <Box alignItems="flex-start" gap="050" display="flex">
                                  <Icon
                                    mt="025"
                                    size="sm"
                                    sizeVersion="v2"
                                    color="base"
                                    name={(result.icon ?? 'search') as IconName}
                                  />
                                  <Flex direction="column" w="full">
                                    <Text text="product-p" truncate>
                                      {result.label}
                                    </Text>
                                    {result.description != null ? (
                                      <Text size="xxs" color="subtle" truncate>
                                        {result.description}
                                      </Text>
                                    ) : null}
                                  </Flex>
                                </Box>
                              </Command.Item>
                            ))}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {(hasSettingsCommands || hasLocalCommands || hasLocalSearchResults) && isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>Searching in {workspace?.name ?? 'this workspace'}…</EmptyStateTitle>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                  {!hasSettingsCommands && !hasLocalCommands && !hasLocalSearchResults && isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>
                          Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                        </EmptyStateTitle>
                        <EmptyStateDescription>
                          Hang on while we search in {workspace?.name ?? 'this workspace'}
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                  {debouncedInputValue.length === 0 && !hasLocalCommands && !hasLocalSearchResults ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">Search {workspace?.name ?? 'Workspace'}</EmptyStateTitle>
                        <EmptyStateDescription>
                          {isBuilder
                            ? 'Try searching for templates, segments, or anything else in this workspace.'
                            : 'Try searching for campaigns, templates, segments, or anything else in this workspace.'}
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {universalSearchShowsNoResultsEmptyState ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">
                          No results found for "<span className="fs-mask">{debouncedInputValue}</span>"
                        </EmptyStateTitle>
                        <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {hasApiResults ? (
                    <>
                      {groupedResults.map(([kind, items], groupIndex) => {
                        const groupOffset = groupedResults
                          .slice(0, groupIndex)
                          .reduce((sum, [, groupItems]) => sum + groupItems.length, 0);
                        return (
                          <Command.Group
                            key={kind}
                            heading={SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`}
                          >
                            {items.map((item, index) => {
                              const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;
                              return (
                                <SearchResultItem
                                  key={key}
                                  item={item}
                                  itemKey={key}
                                  position={computeItemPosition({
                                    hydraCommandsCount: 0,
                                    localSearchCount: activeCategory === 'all' ? localSearchCount : 0,
                                    section: 'apiResults',
                                    groupOffset,
                                    itemIndex: index,
                                  })}
                                  onSelect={handleItemSelect}
                                  catalog={catalogMap}
                                />
                              );
                            })}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {(hasSettingsCommands || hasLocalCommands) && hasApiResults ? (
                    <LocalCommandsSection
                      settingsCommands={settingsCommands}
                      hydraCommands={hydraCommands}
                      baseOffset={localSearchCount + apiResultsCount}
                      onSelect={handleLocalCommandSelect}
                    />
                  ) : null}
                  {askAssistantVisible ? (
                    <Command.Item
                      key="ask-assistant"
                      value="ask-assistant"
                      onSelect={() =>
                        handleAskAssistant(
                          (activeCategory === 'all' ? quickActionsCount + localSearchCount : 0) + apiResultsCount + 1,
                        )
                      }
                      asChild
                    >
                      <Box alignItems="flex-start" gap="050" display="flex">
                        <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                        <Flex direction="column" w="full">
                          <Text text="product-p" truncate w="full">
                            Ask Agent
                          </Text>
                          <Text size="xxs" color="subtle" truncate w="full">
                            Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                          </Text>
                        </Flex>
                      </Box>
                    </Command.Item>
                  ) : null}
                </Command.List>
              </Box>
              <div
                className={clsx(
                  styles.quickLookContainer,
                  (isQuickLookOpen === false || isQuickLookEnabled === false) && styles.quickLookContainerHidden,
                )}
              >
                {isQuickLookOpen && isQuickLookEnabled ? (
                  <QuickLookPanel
                    isLoading={isQuickLookSourcesFetching && !universalSearchShowsNoResultsEmptyState}
                    result={universalSearchShowsNoResultsEmptyState ? null : selectedResult}
                    integrationCatalog={catalogMap}
                  />
                ) : null}
              </div>
            </Flex>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Mod+Enter" />
                <Text size="xxs" color="subtle">
                  Open in new tab
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
